import { Clock } from "lucide-react"
import { useTranslation } from "react-i18next"

import { TiktokEmbed } from "@/components/crew/tiktok-embed"
import { CardBrandBadge } from "@/components/home-triage/card-brand-badge"
import { SubmissionReviewActions } from "@/components/submissions/submission-review-actions"
import { Card, CardContent } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import type { SubmissionReviewItem } from "@/types/home-triage"

/**
 * OKS-707 — One Submissions-to-review card. Mirrors the OKS-706
 * `CandidateReviewCard` structure (shadcn `Card` + `CardContent`,
 * `flex flex-col gap-3`) but embeds the submitted TikTok **video** instead
 * of a profile.
 *
 * Content hierarchy (UX Spec §2, top→bottom):
 *   1. `TiktokEmbed` (OKS-509 client-side video player) keyed by
 *      `tiktok_video_id`, with the thumbnail poster + caption fallback
 *      (never a broken frame);
 *   2. context row — creator `@handle` + mission title;
 *   3. facts line — submitted date (`Intl.DateTimeFormat`) + optional
 *      caption excerpt;
 *   4. stale cue — muted "waiting N days" pill (icon + text, never colour
 *      alone) when past the server stale threshold;
 *   5. action row — Open / Flag / Approve (`SubmissionReviewActions`).
 */
export interface SubmissionReviewCardProps {
  item: SubmissionReviewItem
  className?: string
}

export function SubmissionReviewCard({
  item,
  className,
}: SubmissionReviewCardProps) {
  const { t, i18n } = useTranslation()
  const { submission, creator_handle, mission_title } = item

  const submittedLabel = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: "medium",
  }).format(new Date(submission.submitted_at))

  const handleLabel = creator_handle
    ? `@${creator_handle}`
    : t("submissions.home_review.creator_unknown")

  return (
    <Card
      data-testid="submission-review-card"
      data-submission-id={item.id}
      className={cn(className)}
    >
      <CardContent className="flex flex-col gap-3 pt-6">
        {/* OKS-708 — brand badge (agency cross-brand queue only). */}
        {item.brand && <CardBrandBadge brand={item.brand} />}

        <TiktokEmbed
          videoId={submission.tiktok_video_id}
          thumbnailUrl={submission.tiktok_video_thumbnail_url}
          caption={submission.caption_excerpt ?? mission_title}
        />

        {/* Context row — whose content, for which mission. */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="font-medium">{handleLabel}</span>
          <span className="text-muted-foreground" aria-hidden="true">
            ·
          </span>
          <span className="text-muted-foreground text-sm">{mission_title}</span>
        </div>

        {/* Facts line — submitted date + optional caption excerpt. */}
        <p className="text-muted-foreground text-sm">
          {t("submissions.home_review.facts.submitted", {
            date: submittedLabel,
          })}
          {submission.caption_excerpt ? ` — ${submission.caption_excerpt}` : ""}
        </p>

        {/* Stale cue — icon + text, never colour alone (UX Spec §6). */}
        {item.stale && (
          <span
            data-testid="submission-review-stale"
            className="text-muted-foreground bg-muted inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
          >
            <Clock className="size-3" aria-hidden="true" />
            {t("submissions.home_review.stale", { count: item.waiting_days })}
          </span>
        )}

        <SubmissionReviewActions item={item} className="mt-1" />
      </CardContent>
    </Card>
  )
}
